import { DownOutlined, UpOutlined, UserOutlined } from "@ant-design/icons"
import { Checkbox, Flex, Form, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { brand, neutral } from "../../../../components/widgets/tokens.ts"
import {
  REPORT_ICON_KEYS,
  ReportIconGlyph,
  type ReportIconKey
} from "../../helpers/reportIcons.tsx"
import type { ReportVisibility } from "../../types/types.ts"
import { NewFolderButton } from "../folders/NewFolderButton.tsx"
import { ReportGeneralFields } from "../ReportGeneralFields.tsx"

const { Text } = Typography

export interface WizardGeneralInfo {
  name: string
  description: string
  tags: string[]
  folder: string
  favorite: boolean
  /** Chosen on Review & share; kept here so it survives Back and Continue. */
  visibility: ReportVisibility
  icon: ReportIconKey
}

interface ReportWizardStepGeneralProps {
  value: WizardGeneralInfo
  onChange: (patch: Partial<WizardGeneralInfo>) => void
  folderOptions: string[]
  nameError: boolean
  ownerName: string
}

// Deliberate mockup deviation (user decision 2026-07-17): no Add Widgets panel
// on this step — widgets can only be added on the Build step, so a read-only
// catalog here was confusing.
export const ReportWizardStepGeneral = ({
  value,
  onChange,
  folderOptions,
  nameError,
  ownerName
}: ReportWizardStepGeneralProps) => {
  const { t } = useTranslation()
  const [customizing, setCustomizing] = useState(true)

  return (
    <div className="w-full">
      <Form layout="vertical" requiredMark className="with-default-error">
        <ReportGeneralFields
          value={value}
          onChange={onChange}
          folderOptions={folderOptions}
          nameError={nameError}
          split
          folderAction={
            <NewFolderButton
              existing={folderOptions}
              onCreated={(folder) => onChange({ folder })}
            />
          }
        />

        <section
          className="rounded-lg border border-solid"
          style={{ borderColor: neutral.border }}
        >
          <button
            type="button"
            aria-expanded={customizing}
            onClick={() => setCustomizing((open) => !open)}
            className="flex w-full cursor-pointer items-start gap-3 border-0 bg-transparent px-4 py-3 text-left"
          >
            <span
              aria-hidden
              className="pt-0.5"
              style={{ color: neutral.muted }}
            >
              {customizing ? <UpOutlined /> : <DownOutlined />}
            </span>
            <span className="flex flex-col">
              <Text strong>{t("reports.wizard.general.customization")}</Text>
              <Text type="secondary" className="text-[13px]">
                {t("reports.wizard.general.customizationHint")}
              </Text>
            </span>
          </button>

          {customizing && (
            <div className="flex flex-col gap-5 px-4 pb-4 md:pl-11">
              <div className="flex flex-col gap-5 md:flex-row md:items-start md:gap-8">
                <div className="flex flex-col gap-2">
                  <Text id="report-icon-label">
                    {t("reports.wizard.general.icon")}
                  </Text>
                  <div
                    role="radiogroup"
                    aria-labelledby="report-icon-label"
                    className="flex flex-wrap gap-2"
                  >
                    {REPORT_ICON_KEYS.map((key) => {
                      const active = value.icon === key
                      return (
                        <button
                          key={key}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          aria-label={t(`reports.wizard.general.icons.${key}`)}
                          onClick={() => onChange({ icon: key })}
                          className="flex size-12 cursor-pointer items-center justify-center rounded-lg text-xl"
                          style={{
                            color: active ? brand.teal : neutral.muted,
                            background: active
                              ? "rgba(42,125,140,.10)"
                              : "#fff",
                            border: `1px solid ${active ? brand.teal : neutral.border}`
                          }}
                        >
                          <ReportIconGlyph iconKey={key} />
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div
                  className="flex flex-col gap-1 md:border-0 md:border-l md:border-solid md:pl-8"
                  style={{ borderColor: neutral.border }}
                >
                  <Checkbox
                    checked={value.favorite}
                    onChange={(e) => onChange({ favorite: e.target.checked })}
                  >
                    {t("reports.wizard.general.favorite")}
                  </Checkbox>
                  <Text type="secondary" className="pl-6 text-[13px]">
                    {t("reports.wizard.general.favoriteHint")}
                  </Text>
                </div>
              </div>

              <Flex align="center" gap="middle" wrap="wrap">
                <Text strong>{t("reports.wizard.general.owner")}</Text>
                <span
                  className="inline-flex min-w-[240px] items-center gap-2 rounded-md border border-solid px-3 py-1.5"
                  style={{
                    borderColor: neutral.border,
                    background: neutral.surface
                  }}
                >
                  <UserOutlined aria-hidden style={{ color: neutral.muted }} />
                  <Text>{ownerName}</Text>
                </span>
              </Flex>
            </div>
          )}
        </section>
      </Form>
    </div>
  )
}
